import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getComplianceRules } from '@/lib/api/amazon-listing'
import type { CompiledFieldRules, CompiledPattern, CompiledRules, CompiledStyleRule } from './validation.types'
import type { FieldRules } from '@/lib/api/amazon-listing.types'

function compileField(fr: FieldRules): CompiledFieldRules {
  const forbiddenPatterns: CompiledPattern[] = (fr.forbidden_patterns ?? []).map((p) => ({
    regex: new RegExp(p.pattern, 'gi'), severity: p.severity, category: p.category,
  }))
  const styleRules: CompiledStyleRule[] = (fr.style_rules ?? []).map((s) => ({
    rule: s.rule, regex: new RegExp(s.pattern, 'gi'), severity: s.severity,
  }))
  return {
    maxBytes: fr.max_bytes,
    maxChars: fr.max_chars ?? fr.max_bytes,
    optimalMinChars: fr.optimal_min_chars,
    optimalMaxChars: fr.optimal_max_chars,
    forbiddenPatterns,
    styleRules,
  }
}

export function useComplianceRules(marketplace: string | undefined) {
  const { data, isLoading } = useQuery({
    queryKey: ['compliance-rules', marketplace],
    queryFn: () => getComplianceRules(marketplace!),
    enabled: !!marketplace,
    staleTime: Infinity,
  })

  const rules = useMemo<CompiledRules | undefined>(() => {
    if (!data) return undefined
    return {
      title: compileField(data.title),
      bullets: { ...compileField(data.bullets), max_count: data.bullets.max_count },
      description: compileField(data.description),
    }
  }, [data])

  return { rules, isLoading }
}
